/*
 *  (c) 2015 - 2023 ENISCO by FORCAM GmbH
 */

import React, {useState} from 'react'
import {ListItemIcon, ListItemText, Menu, MenuItem} from '@mui/material'
import {Favorite, Public, ResetTv, Tune} from '@mui/icons-material'
import {HardlinkItem} from './hardlink/HardlinkItem'
import {FormattedMessage} from 'react-intl'
import {QuickLaunchMenu} from './quickLaunchMenu/QuickLaunchMenu'
import {
  asPermission,
  clone,
  cloneSafe,
  useError,
  useParameters,
  usePermissions,
  useResolver,
  useUserId
} from '@enisco/mcc-react-lib'
import useFavoriteId from '../../../hooks/useFavoriteId'
import {UpdateFavorite} from './favorites/UpdateFavorite'
import FavoriteDialog from './favorites/dialog/FavoriteDialog'
import useCurrentApp from '../../../hooks/useCurrentApp'
import useLoadProfile from '../../../hooks/useLoadProfile'
import {DefaultParameter} from '../../../model/defaultParameters'
import useDefaultParameters from '../../../hooks/useDefaultParameters'
import {useNavigate} from 'react-router-dom'
import useFavoriteApps from '../../../hooks/useFavoriteApps'

export const AppContextMenu: React.FC<{
  anchorEl: Element | undefined
  setAnchorEl: (el: Element | undefined) => void
}> = ({anchorEl, setAnchorEl}) => {
  const resolver = useResolver()
  const error = useError()
  const userId = useUserId()
  const [app] = useCurrentApp()
  const [parameters] = useParameters()
  const [favoriteId] = useFavoriteId()
  const [favoriteApps] = useFavoriteApps()
  const [defaultParameters] = useDefaultParameters()
  const [personalFavDialog, setPersonalFavDialog] = useState(false)
  const [globalFavDialog, setGlobalFavDialog] = useState(false)
  const [loadProfile] = useLoadProfile()
  const navigate = useNavigate()
  const hasAccess = usePermissions()

  const openPersonalFavDialog = (open: boolean) => {
    setPersonalFavDialog(open)
    closeMenu()
  }

  const openGlobalFavDialog = (open: boolean) => {
    setGlobalFavDialog(open)
    closeMenu()
  }

  const checkIfCurrentAppHasDefault = () => {
    return defaultParameters
      ? defaultParameters?.find(
          (para) => para.app === app?.app && para.module === app?.module
        )
      : undefined
  }

  const saveAsDefault = () => {
    const defaultParameter = checkIfCurrentAppHasDefault()

    if (defaultParameter) {
      const defaultPara = clone(defaultParameter)
      defaultPara.parameters = parameters

      resolver(
        'mcc-portal-api',
        '/internal/default-parameters/' + defaultPara.id,
        {
          options: {
            method: 'PUT',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(defaultPara)
          }
        }
      )
        .then(() => {
          closeMenu()
        })
        .finally(() => {
          return loadProfile()
        })
        .catch(error)

      return
    }

    const newDefaultParameter = {
      app: app?.app,
      module: app?.module,
      parameters: parameters
    } as DefaultParameter
    resolver('mcc-portal-api', '/internal/default-parameters', {
      options: {
        method: 'POST',
        headers: {'Content-Type': 'application/json'},
        body: JSON.stringify(newDefaultParameter)
      }
    })
      .then((response: {json: () => any}) => response.json())
      .then(() => {
        closeMenu()
      })
      .finally(() => {
        return loadProfile()
      })
      .catch(error)
  }

  const resetToFactoryDefault = () => {
    const defaultParameter = checkIfCurrentAppHasDefault()
    if (defaultParameter) {
      const defaultParameterToDelete = cloneSafe(defaultParameter)
      resolver(
        'mcc-portal-api',
        '/internal/default-parameters/' + defaultParameter?.id,
        {
          options: {
            method: 'DELETE',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify(defaultParameterToDelete)
          }
        }
      )
        .then(() => {
          closeMenu()
          navigate(0)
        })
        .finally(() => {
          return loadProfile()
        })
        .catch(error)
    }
  }

  const closeMenu = () => {
    setAnchorEl(undefined)
  }

  const canUpdateFavorite = () => {
    const favorite = favoriteApps.find((fav) => fav.id === favoriteId)
    if (favorite) {
      return (
        Boolean(favorite.user) ||
        hasAccess(asPermission('mcc-portal', '_global-favorites', '_edit'))
      )
    }

    return false
  }

  return (
    <div data-testid={'AppContextMenu:root'}>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        keepMounted
        onClose={closeMenu}
      >
        {userId ? (
          <MenuItem onClick={() => openPersonalFavDialog(true)}>
            <ListItemIcon>
              <Favorite color={'primary'} />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage
                id='TitleBarMenu.PersonalFavorite'
                defaultMessage='Add to personal favorites'
              />
            </ListItemText>
          </MenuItem>
        ) : undefined}
        {hasAccess(asPermission('mcc-portal', '_global-favorites', '_edit')) ? (
          <MenuItem onClick={() => openGlobalFavDialog(true)}>
            <ListItemIcon>
              <Public color={'primary'} />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage
                id='TitleBarMenu.GlobalFavorite'
                defaultMessage='Add to global favorites'
              />
            </ListItemText>
          </MenuItem>
        ) : undefined}
        {favoriteId && canUpdateFavorite() ? (
          <UpdateFavorite onClose={() => closeMenu()} favoriteId={favoriteId} />
        ) : undefined}
        {userId ? (
          <QuickLaunchMenu
            onClose={() => closeMenu()}
            open={Boolean(anchorEl)}
          />
        ) : undefined}
        <HardlinkItem onClose={() => closeMenu()} />
        {hasAccess(asPermission('mcc-portal', '_default-app-settings', '_edit')) ? (
          <MenuItem onClick={saveAsDefault}>
            <ListItemIcon>
              <Tune color={'primary'} />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage
                id='TitleBarMenu.SaveAsAppDefault'
                defaultMessage='Save current app settings as default'
              />
            </ListItemText>
          </MenuItem>
        ) : undefined}
        {checkIfCurrentAppHasDefault() &&
        hasAccess(asPermission('mcc-portal', '_default-app-settings', '_edit')) ? (
          <MenuItem onClick={resetToFactoryDefault}>
            <ListItemIcon>
              <ResetTv color={'primary'} />
            </ListItemIcon>
            <ListItemText>
              <FormattedMessage
                id='TitleBarMenu.ResetToFactoryDefault'
                defaultMessage='Reset app to factory default'
              />
            </ListItemText>
          </MenuItem>
        ) : undefined}
      </Menu>
      <FavoriteDialog
        open={personalFavDialog}
        onClose={() => openPersonalFavDialog(false)}
        user={userId}
      />
      <FavoriteDialog
        open={globalFavDialog}
        onClose={() => openGlobalFavDialog(false)}
      />
    </div>
  )
}
